import { create } from 'zustand';

import { shareInFlight, type InFlightMap } from '../../api/core/inflight';
import { IztContentRepository } from '../../api/repository/izt-content';
import type { IztContentTreeParams } from '../../api/repository/izt-content';
import type { IztContentNode } from '../../api/types';
import { userErrorMessage } from '../userError';

function queryKey(query: IztContentTreeParams): string {
  return `${String(query.class)}|${String(query.grade || '')}|${String(query.subject || '')}`;
}

// the home page asks for today's class twice at mount (suggestions and courses); one request serves both
const inFlight: InFlightMap<IztContentNode[]> = {};

function requestTree(query: IztContentTreeParams): Promise<IztContentNode[]> {
  return shareInFlight(inFlight, queryKey(query), () => IztContentRepository.getTree(query));
}

export interface QltIztContentState {
  tree: IztContentNode[];
  cache: Record<string, IztContentNode[]>;
  loading: boolean;
  error: string | null;
  loadTree: (query: IztContentTreeParams) => Promise<IztContentNode[]>;
  /** Cached read that leaves tree/loading/error alone and rejects on failure, for callers spanning several classes. */
  fetchTree: (query: IztContentTreeParams) => Promise<IztContentNode[]>;
  clear: () => void;
}

/** The query the panel last asked for; only its answer may become `tree`. */
let latestTreeKey = '';

export const useQltIztContentStore = create<QltIztContentState>((set, get) => ({
  tree: [],
  cache: {},
  loading: false,
  error: null,
  async loadTree(query) {
    const key = queryKey(query);
    latestTreeKey = key;
    const cached = get().cache[key];
    if (cached) {
      set({ tree: cached, error: null, loading: false });
      return cached;
    }
    set({ loading: true, error: null, tree: [] });
    try {
      const tree = await requestTree(query);
      // a scope that resolved late (timetable fallback) must not be overwritten by the earlier, narrower query
      set((state) => (latestTreeKey === key ? { tree, cache: { ...state.cache, [key]: tree }, loading: false } : { cache: { ...state.cache, [key]: tree } }));
      return tree;
    } catch (error) {
      if (latestTreeKey === key) set({ loading: false, error: userErrorMessage(error) });
      return [];
    }
  },
  async fetchTree(query) {
    const key = queryKey(query);
    const cached = get().cache[key];
    if (cached) return cached;
    const tree = await requestTree(query);
    set((state) => ({ cache: { ...state.cache, [key]: tree } }));
    return tree;
  },
  clear: () => set({ tree: [], error: null }),
}));
